import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AppUser } from '@/hooks/useUsuarios';
import { ADMIN_USER_STATUS_META } from '@/lib/admin-users';
import { Columns3, Search, Sparkles, X } from 'lucide-react';

interface UsuariosAdminToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  statusFilter: 'ALL' | AppUser['status'];
  onStatusFilterChange: (value: 'ALL' | AppUser['status']) => void;
  columns: ColumnDef<AppUser>[];
  visibleColumns: string[];
  onColumnToggle: (columnId: string) => void;
  totalUsuarios: number;
  filteredUsuarios: number;
  countsByStatus: Record<AppUser['status'], number>;
  rightAction?: React.ReactNode;
}

const STATUS_FILTERS: Array<{ value: 'ALL' | AppUser['status']; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'APPROVED', label: 'Aprovados' },
  { value: 'PENDING', label: 'Pendentes' },
  { value: 'REJECTED', label: 'Rejeitados' },
  { value: 'INACTIVE', label: 'Inativos' },
];

export function UsuariosAdminToolbar({
  searchValue,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  columns,
  visibleColumns,
  onColumnToggle,
  totalUsuarios,
  filteredUsuarios,
  countsByStatus,
  rightAction,
}: UsuariosAdminToolbarProps) {
  const toggleableColumns = columns.filter(
    (col) => col.id !== 'select' && col.id !== 'actions' && (col.id || (col as { accessorKey?: string }).accessorKey)
  );

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1 space-y-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <div className="relative w-full xl:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, email, papel ou status..."
                value={searchValue}
                onChange={(event) => onSearchChange(event.target.value)}
                className="h-11 rounded-xl border-input bg-background pl-9 pr-10 shadow-sm focus-visible:ring-emerald-500/20"
              />
              {searchValue && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onSearchChange('')}
                  className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 rounded-full"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="h-11 rounded-xl border-input bg-background px-4 shadow-sm">
                  <Columns3 className="mr-2 h-4 w-4" />
                  Colunas
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel>Exibir colunas</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {toggleableColumns.map((column) => {
                  const columnId = (column.id || (column as { accessorKey?: string }).accessorKey) as string;

                  return (
                    <DropdownMenuCheckboxItem
                      key={columnId}
                      checked={visibleColumns.includes(columnId)}
                      onCheckedChange={() => onColumnToggle(columnId)}
                      className="rounded-lg my-0.5"
                    >
                      {typeof column.header === 'string'
                        ? column.header
                        : columnId.charAt(0).toUpperCase() + columnId.slice(1)}
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((filter) => {
              const isActive = statusFilter === filter.value;
              const count =
                filter.value === 'ALL'
                  ? totalUsuarios
                  : countsByStatus[filter.value as AppUser['status']] || 0;
              const meta =
                filter.value === 'ALL'
                  ? null
                  : ADMIN_USER_STATUS_META[filter.value as AppUser['status']];

              return (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => onStatusFilterChange(filter.value)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                    isActive
                      ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shadow-sm'
                      : 'border-input bg-background text-muted-foreground hover:border-accent-foreground/20 hover:bg-accent'
                  }`}
                >
                  {meta && <span className={`h-2.5 w-2.5 rounded-full ${meta.subtleTone}`} />}
                  <span>{filter.label}</span>
                  <Badge variant="secondary" className="rounded-full bg-muted text-muted-foreground px-1.5 min-w-[20px]">
                    {count}
                  </Badge>
                </button>
              );
            })}
          </div>
        </div>

        {rightAction && <div className="flex-shrink-0">{rightAction}</div>}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-dashed border-border pt-4 text-sm text-muted-foreground">
        <div className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-muted-foreground font-medium">
          <Sparkles className="h-3.5 w-3.5 text-blue-500" />
          {filteredUsuarios === totalUsuarios
            ? `${totalUsuarios} usuários monitorados`
            : `${filteredUsuarios} de ${totalUsuarios} usuários visíveis`}
        </div>

        {searchValue && (
          <Badge variant="outline" className="rounded-full border-emerald-500/20 bg-emerald-500/5 px-3 py-1 text-emerald-600 dark:text-emerald-400">
            Busca: &quot;{searchValue}&quot;
          </Badge>
        )}

        {statusFilter !== 'ALL' && (
          <Badge variant="outline" className="rounded-full border-border bg-background px-3 py-1">
            Filtro: {ADMIN_USER_STATUS_META[statusFilter].label}
          </Badge>
        )}
      </div>
    </div>
  );
}
